import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import TreeSelect from '@components/uikit/TreeSelect/TreeSelect';

import { groupSelectAllValue } from '@constants/groupSelectAllValue';
import { locationSelectAllValue } from '@constants/locationSelectAllValue';

import { getFacilityStructure } from '@helpers/getFacilityStructure';

import useTranslation from '@hooks/useTranslation';

import type { ILocation } from '@model/location';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useFacilitiesListQuery } from '@services/useFacilitiesListQuery';
import { useFacilityGroupsQuery } from '@services/useFacilityGroupsQuery';

import { selectAppLocationSelectValue, useAppActions } from '@store/useAppStore';
import { useAppPersistActions } from '@store/useAppStorePersist';

import { useMapActions } from '../../../Maps/store/useMapStore';
import {
  selectGroupFacilitySwitcher,
  useGroupFacilitySwitcherActions,
} from '../../store/useGroupFacilitySwitcherStore';
import GroupFacilitySwitcher from '../GroupFacilitySwitcher/GroupFacilitySwitcher';
import style from './style.module.scss';

const LocationSelect = ({ type = '' }) => {
  const { t } = useTranslation();

  const { setAppLocationSelectValue } = useAppActions();
  const { setMapGeoState } = useMapActions();
  const { setAppPersistMapsVisible } = useAppPersistActions();
  const { data: facilityListData } = useFacilitiesListQuery();

  const locationSelectValue = selectAppLocationSelectValue();
  const groupFacilitySwitcherValue = selectGroupFacilitySwitcher();

  const { setGroupFacilitySwitcher } = useGroupFacilitySwitcherActions();

  const { data: assetTreeData } = useAssetTreeQuery();
  const { data: facilityGroupsData } = useFacilityGroupsQuery();

  const facilityStructureData = assetTreeData && assetTreeData.facilities;

  useEffect(() => {
    if (!assetTreeData || !facilityListData) return;

    if (facilityStructureData?.length === 1) {
      setAppLocationSelectValue(facilityStructureData?.[0]);
      setAppPersistMapsVisible(false);
      setMapState(facilityStructureData?.[0].facilityId);

      setAppLocationSelectValue(locationSelectAllValue);
    }
  }, [assetTreeData, facilityListData]);

  const setMapState = (facilityId: string | number) => {
    const { name, country } = facilityListData?.[facilityId] || {};
    setMapGeoState({ type: 'country', name: country?.name, facilityName: name });
  };

  const isFilterByFacility = groupFacilitySwitcherValue === 'byFacility';
  const structureData = useMemo(() => {
    if (!isFilterByFacility) {
      return getFacilityStructure(facilityGroupsData?.data || [], '', 'groups', t);
    }

    return getFacilityStructure(facilityStructureData, type, 'facility', t);
  }, [isFilterByFacility, facilityStructureData, facilityGroupsData, type]);

  const locName = locationSelectValue.id === 'all' ? t(locationSelectValue.name) : locationSelectValue.name;

  const setSelectValue = (id: string, node: ILocation) => {
    setAppLocationSelectValue({ ...node, filterIds: node.facilitiesIds || [node.id] });

    if (id !== 'all') {
      setMapState(node.facilityId);
    }

    setAppPersistMapsVisible(false);
  };

  const handleSwitchType = (value: 'byFacility' | 'byGroup') => {
    setGroupFacilitySwitcher(value);

    if (type === 'only-facility' && value === 'byFacility') {
      const firstLoc: any = facilityStructureData?.[0];

      setAppLocationSelectValue({ ...firstLoc, filterIds: [firstLoc.id] });
      return;
    }

    setAppLocationSelectValue(
      value === 'byFacility'
        ? locationSelectAllValue
        : { ...groupSelectAllValue, filterIds: facilityGroupsData?.allFacilitiesIdsByGroups },
    );
  };

  return (
    <div id="tourStep-3" className={style.locationSelectBlock}>
      <GroupFacilitySwitcher
        switchValue={groupFacilitySwitcherValue}
        onChangeSwitchValue={handleSwitchType}
        showGroups={!!facilityGroupsData?.data?.length}
      />
      <TreeSelect
        value={locationSelectValue.id}
        selectedName={locName}
        onChange={setSelectValue}
        fieldNames={{
          children: 'children',
          title: 'name',
          key: groupFacilitySwitcherValue === 'byFacility' ? 'id' : 'treeKey',
        }}
        items={structureData}
        name="location-select"
        variant="header-treeselect"
        type={'woHelperText'}
        searchable
      />
    </div>
  );
};

export default LocationSelect;
